/**
 * Pure operations on a step's line list, matching the server validation in
 * GuidesAPI::updateStepLines. The line list is a flat array where nesting is
 * expressed by `level`; a line's subtree is the run of following lines with
 * a greater level.
 *
 * The rules:
 * - max 8 lines per step, max nesting level 2 (three levels)
 * - indenting requires an older sibling at the same level, so the first
 *   line can never indent and you can't skip a level
 * - indent/dedent moves the whole subtree, not just the line
 * - no deleting the last line or a line with children
 * - 1-350 characters per line, with `[...]` wiki tokens excluded from the
 *   count (same greedy regex as the server)
 */

export type EditorLine = {
   /** Stable client-side key for React lists; not the server lineid. */
   key: string;
   text: string;
   bullet: string;
   level: number;
   /** Server-rendered HTML preview; null until first rendered. */
   rendered: string | null;
};

export const MAX_LINES = 8;
export const MAX_LINE_CHARS = 350;
const MAX_LEVEL = 2;

const keyCounter = { next: 0 };
function newLineKey(): string {
   keyCounter.next += 1;
   return `line-${keyCounter.next}`;
}

/**
 * Character count as the server counts it: trimmed, with wiki tokens
 * removed by the same greedy pattern (`\[.*\]`) GuidesAPI uses. Greedy
 * means everything from the first `[` to the last `]` goes — text BETWEEN
 * two tokens doesn't survive the count (`a[link]b[br]c` counts `ac`, 2).
 */
export function visibleLength(text: string): number {
   return text.trim().replace(/\[.*\]/g, '').length;
}

/** Index just past the last descendant of the line at `index`. */
function subtreeEnd(lines: EditorLine[], index: number): number {
   const level = lines[index].level;
   let end = index + 1;
   while (end < lines.length && lines[end].level > level) {
      end++;
   }
   return end;
}

/**
 * The nearest preceding sibling at the same level, or -1. Stops when a
 * shallower line is found, since that ends the sibling group.
 */
function previousSiblingIndex(lines: EditorLine[], index: number): number {
   const level = lines[index].level;
   for (let i = index - 1; i >= 0; i--) {
      if (lines[i].level < level) {
         return -1;
      }
      if (lines[i].level === level) {
         return i;
      }
   }
   return -1;
}

/** The nearest following sibling at the same level, or -1. */
function nextSiblingIndex(lines: EditorLine[], index: number): number {
   const level = lines[index].level;
   for (let i = subtreeEnd(lines, index); i < lines.length; i++) {
      if (lines[i].level < level) {
         return -1;
      }
      if (lines[i].level === level) {
         return i;
      }
   }
   return -1;
}

/** Deepest level within the line's subtree, including the line itself. */
function deepestLevel(lines: EditorLine[], index: number): number {
   let deepest = lines[index].level;
   for (let i = index + 1; i < subtreeEnd(lines, index); i++) {
      deepest = Math.max(deepest, lines[i].level);
   }
   return deepest;
}

export function canIndent(lines: EditorLine[], index: number): boolean {
   return previousSiblingIndex(lines, index) !== -1 && deepestLevel(lines, index) < MAX_LEVEL;
}

/** Indents the line and its whole subtree one level. */
export function indentLine(lines: EditorLine[], index: number): EditorLine[] {
   if (!canIndent(lines, index)) {
      return lines;
   }
   const end = subtreeEnd(lines, index);
   return lines.map((line, i) =>
      i >= index && i < end ? { ...line, level: line.level + 1 } : line
   );
}

export function canDedent(lines: EditorLine[], index: number): boolean {
   return lines[index].level > 0;
}

/** Dedents the line and its whole subtree one level. */
export function dedentLine(lines: EditorLine[], index: number): EditorLine[] {
   if (!canDedent(lines, index)) {
      return lines;
   }
   const end = subtreeEnd(lines, index);
   return lines.map((line, i) =>
      i >= index && i < end ? { ...line, level: line.level - 1 } : line
   );
}

export function canDelete(lines: EditorLine[], index: number): boolean {
   const hasChildren = index + 1 < lines.length && lines[index + 1].level > lines[index].level;
   return lines.length > 1 && !hasChildren;
}

export function deleteLine(lines: EditorLine[], index: number): EditorLine[] {
   if (!canDelete(lines, index)) {
      return lines;
   }
   return lines.filter((_, i) => i !== index);
}

export function canAddLine(lines: EditorLine[]): boolean {
   return lines.length < MAX_LINES;
}

export function makeLine(partial: Partial<EditorLine> = {}): EditorLine {
   return {
      key: newLineKey(),
      text: '',
      bullet: 'black',
      level: 0,
      rendered: null,
      ...partial,
   };
}

/**
 * Inserts a new line after the line at `index` (after its own slot, not its
 * subtree). The new line joins the following line's level when
 * that line is deeper, so adding below a parent extends its children.
 */
export function insertLineBelow(lines: EditorLine[], index: number): EditorLine[] {
   if (!canAddLine(lines)) {
      return lines;
   }
   const next = lines[index + 1];
   const level = next && next.level > lines[index].level ? next.level : lines[index].level;
   const inserted = makeLine({ level });
   return [...lines.slice(0, index + 1), inserted, ...lines.slice(index + 1)];
}

/** Inserts a new line above the line at `index`, at the same level. */
export function insertLineAbove(lines: EditorLine[], index: number): EditorLine[] {
   if (!canAddLine(lines)) {
      return lines;
   }
   const inserted = makeLine({ level: lines[index].level });
   return [...lines.slice(0, index), inserted, ...lines.slice(index)];
}

export function canMoveUp(lines: EditorLine[], index: number): boolean {
   return previousSiblingIndex(lines, index) !== -1;
}

/** Swaps the line's subtree with the previous sibling's subtree. */
export function moveLineUp(lines: EditorLine[], index: number): EditorLine[] {
   const siblingIndex = previousSiblingIndex(lines, index);
   if (siblingIndex === -1) {
      return lines;
   }
   const end = subtreeEnd(lines, index);
   return [
      ...lines.slice(0, siblingIndex),
      ...lines.slice(index, end),
      ...lines.slice(siblingIndex, index),
      ...lines.slice(end),
   ];
}

export function canMoveDown(lines: EditorLine[], index: number): boolean {
   return nextSiblingIndex(lines, index) !== -1;
}

/** Swaps the line's subtree with the next sibling's subtree. */
export function moveLineDown(lines: EditorLine[], index: number): EditorLine[] {
   const siblingIndex = nextSiblingIndex(lines, index);
   if (siblingIndex === -1) {
      return lines;
   }
   const end = subtreeEnd(lines, index);
   const siblingEnd = subtreeEnd(lines, siblingIndex);
   return [
      ...lines.slice(0, index),
      ...lines.slice(siblingIndex, siblingEnd),
      ...lines.slice(index, end),
      ...lines.slice(siblingEnd),
   ];
}

/** The end (exclusive) of the subtree rooted at `index`, for drag ranges. */
export function subtreeBounds(lines: EditorLine[], index: number): { start: number; end: number } {
   return { start: index, end: subtreeEnd(lines, index) };
}

/**
 * The root levels the subtree at `fromIndex` may take when dropped into
 * `gap` — an insertion point in the list WITHOUT that subtree (0 = before
 * the first remaining line, rest.length = after the last). The bounds are
 * the same invariants indent/dedent/move enforce, applied to an arbitrary
 * position:
 * - a line sits at most one level below its predecessor (`canIndent`'s
 *   older-sibling rule), so the root caps at prev.level + 1;
 * - the whole subtree keeps its shape, so the root caps where its deepest
 *   line would pass MAX_LEVEL;
 * - the line after the drop must not be left skipping levels, which bounds
 *   the root from below via the subtree's last line.
 * Null when the gap can't legally hold the subtree at any level.
 */
export function dropLevelRange(
   lines: EditorLine[],
   fromIndex: number,
   gap: number
): { min: number; max: number } | null {
   const { start, end } = subtreeBounds(lines, fromIndex);
   const rest = [...lines.slice(0, start), ...lines.slice(end)];
   if (gap < 0 || gap > rest.length) {
      return null;
   }
   const rootLevel = lines[start].level;
   const deepestRel = deepestLevel(lines, start) - rootLevel;
   const lastRel = lines[end - 1].level - rootLevel;

   const prev = rest[gap - 1];
   const next = rest[gap];
   const max = Math.min(prev ? prev.level + 1 : 0, MAX_LEVEL - deepestRel);
   const min = next ? Math.max(0, next.level - 1 - lastRel) : 0;
   return min <= max ? { min, max } : null;
}

/**
 * Moves the subtree at `fromIndex` into `gap` (see dropLevelRange) with its
 * root at `level`, shifting the whole subtree by the same amount. Returns
 * the list unchanged when the drop is out of range or the level illegal.
 */
export function dropSubtree(
   lines: EditorLine[],
   fromIndex: number,
   gap: number,
   level: number
): EditorLine[] {
   const range = dropLevelRange(lines, fromIndex, gap);
   if (!range || level < range.min || level > range.max) {
      return lines;
   }
   const { start, end } = subtreeBounds(lines, fromIndex);
   const shift = level - lines[start].level;
   const subtree = lines
      .slice(start, end)
      .map(line => (shift === 0 ? line : { ...line, level: line.level + shift }));
   const rest = [...lines.slice(0, start), ...lines.slice(end)];
   return [...rest.slice(0, gap), ...subtree, ...rest.slice(gap)];
}

export type LineProblem =
   | { code: 'no-lines' }
   | { code: 'too-many-lines' }
   | { code: 'line-too-long'; line: number };

/**
 * Whether a line has no visible text. Blank lines are dropped on save
 * (buildStepPayload) rather than flagged, so they never block it.
 */
export function isBlankLine(line: EditorLine): boolean {
   return line.text.trim().length === 0;
}

/**
 * Blocking problems the server would reject the save for; shown next to the
 * Save button so nobody burns a request on a payload we know is invalid.
 * Structured (not strings) so the UI layer owns the translations.
 *
 * A blank line among content is silently dropped on save (buildStepPayload),
 * so it doesn't count toward the line limit or block the save. But a step
 * that's all blank lines has nothing to send: the server rejects any line
 * under MIN_LINE_LENGTH (1 char), so an otherwise-empty step 422s on the
 * placeholder line the editor always carries. Require at least one non-blank
 * line so that surfaces here instead of as a failed request.
 */
export function validateLines(lines: EditorLine[]): LineProblem[] {
   const problems: LineProblem[] = [];
   const contentLines = lines.filter(line => !isBlankLine(line));
   if (contentLines.length === 0) {
      problems.push({ code: 'no-lines' });
   }
   if (contentLines.length > MAX_LINES) {
      problems.push({ code: 'too-many-lines' });
   }
   lines.forEach((line, index) => {
      if (!isBlankLine(line) && visibleLength(line.text) > MAX_LINE_CHARS) {
         problems.push({ code: 'line-too-long', line: index + 1 });
      }
   });
   return problems;
}
